// =====================================================================
// src/lib/fnrh.js — o módulo FNRH (0062), sem DOM
//
// Hugo, 25/09/2026: "1A" — a ficha completa DENTRO do sistema (tela,
// fichas incompletas, PDF e planilha), sem envio ao MTur; "garvey é
// condomínio, mas queremos utilizar a ficha como padrão".
//
// O banco (0062, `fnrh_do_periodo`) devolve uma linha por HÓSPEDE das
// reservas confirmadas/concluídas com check-in no período, já cortada por
// perfil (proprietário nunca; imobiliária só as dela). Este arquivo:
//   - diz o que falta em cada ficha — pela MESMA régua de Hóspedes e do
//     balcão (`faltaNaFicha`), senão "completa" numa tela e "incompleta"
//     na outra;
//   - conta os números do topo;
//   - filtra (texto e "só incompletas");
//   - monta as linhas do PDF/planilha e a ficha de UMA pessoa.
//
// O MdM não tem FNRH (é de flats): não há tela para copiar. O desenho usa
// os componentes dele que este app já tem — `.stat-card`, `table.table-cards`,
// `.badge-*`, o filtro De/Até com atalhos da Auditoria.
// =====================================================================
import { faltaNaFicha, CAMPOS_FNRH_OBRIGATORIOS, formatarDocumento, idade, ehLegado, textoAntigo, residencia } from './hospedes.js';
import { nomeDoPais, BRASIL, formatarCep } from './ibge.js';
import { rotuloMotivo, rotuloTransporte, rotuloSexo, rotuloRaca, rotuloDeficiencia, rotuloTipoDeficiencia } from './precheckin.js';
import { horaDe, diaDe } from './auditoria.js';

// Os rótulos das colunas da ficha, na ordem da ficha impressa
export const ROTULOS = Object.freeze({
  nome_social: 'Nome social', sexo: 'Gênero', raca: 'Raça/cor', nacionalidade_pais: 'Nacionalidade', profissao: 'Profissão',
  // 0070
  residencia_pais: 'País onde mora', municipio_id: 'Município', cidade: 'Cidade', logradouro: 'Logradouro', numero_endereco: 'Número',
  complemento: 'Complemento', bairro: 'Bairro', cep: 'CEP', procedencia: 'Procedência', destino: 'Próximo destino',
  motivo_viagem: 'Motivo da viagem', meio_transporte: 'Meio de transporte',
  // 0065
  documento: 'Documento', genero_descricao: 'Descrição do gênero', deficiencia: 'Deficiência', tipo_deficiencia: 'Tipo de deficiência',
});

// `coleta` (0065) é o `coleta_raca` do hotel — o módulo FNRH contratado.
// Com ele a régua da ficha completa inclui documento, raça/cor e
// deficiência (o que a API exige). Todas as funções abaixo a recebem e a
// repassam para `faltaNaFicha`: uma régua só, em todas as telas.

const txt = v => String(v == null ? '' : v).trim();
const br = iso => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : '');
// A chegada e a saída no fuso do HOTEL (CLAUDE.md §6) — nunca o
// `.toISOString()`, que às 22h de Brasília já é o dia seguinte em UTC
const hora = (ts, fuso) => (ts && diaDe(ts, fuso) ? `${br(diaDe(ts, fuso))} ${horaDe(ts, fuso)}` : '');

// O que falta na ficha desta pessoa, em PALAVRAS (a recepção pergunta ao
// hóspede, não lê chave de coluna). O documento entra à parte: sem ele a
// chave não sai (0017), e o menor acompanhado pode não ter (0041).
// sigla continua sigla: "UF", "CEP" — não "uf", "cep"
const minuscula = r => (r === r.toUpperCase() ? r : r[0].toLowerCase() + r.slice(1));
// 0070: o que tem TEXTO ANTIGO sai com ele — "nacionalidade (antes:
// Americana)" —, para a recepção perguntar sabendo o que estava escrito.
export function faltas(l, coleta = false) {
  const antigo = textoAntigo(l);
  return faltaNaFicha(l, coleta).map(c => (ehLegado(c, txt((l || {})[c]))
    ? `${minuscula(ROTULOS[c] || c)} (a lista mudou — escolher de novo)`
    : antigo[c] ? `${minuscula(ROTULOS[c] || c)} (antes: ${antigo[c]})`
    : minuscula(ROTULOS[c] || c)));
}
// A nacionalidade para mostrar: o país da lista, ou o texto antigo marcado
const nacionalidade = l => nomeDoPais(l.nacionalidade_pais) || (txt(l.nacionalidade) ? `${txt(l.nacionalidade)} (texto antigo)` : '');
export const completa = (l, coleta = false) => faltaNaFicha(l, coleta).length === 0;
export const semDocumento = l => !txt((l || {}).documento);

// Os números do topo. "Hóspedes" conta GENTE; "reservas" conta reserva.
export function resumo(linhas, coleta = false) {
  const l = (linhas || []).filter(Boolean);
  return {
    hospedes: l.length,
    reservas: new Set(l.map(x => x.reserva_id)).size,
    completas: l.filter(x => completa(x, coleta)).length,
    incompletas: l.filter(x => !completa(x, coleta)).length,
    menores: l.filter(x => x.menor).length,
    incapazes: l.filter(x => x.legalmente_incapaz).length,
  };
}

// Filtro da tela: texto (quarto, nome, nome social, documento) e "só
// incompletas". O documento se procura pelos DÍGITOS — a recepção digita
// "123.456" ou "123456" e os dois têm de achar.
export function filtrar(linhas, { texto = '', soIncompletas = false, coleta = false } = {}) {
  const t = txt(texto).toLowerCase();
  const dig = t.replace(/\D/g, '');
  return (linhas || []).filter(l => {
    if (soIncompletas && completa(l, coleta)) return false;
    if (!t) return true;
    const alvo = [l.quarto, l.nome, l.nome_social, l.reserva_numero].map(x => txt(x).toLowerCase()).join(' ');
    return alvo.includes(t) || (dig.length >= 3 && txt(l.documento).replace(/\D/g, '').includes(dig));
  });
}

export const rotuloFicha = (l, coleta = false) => (completa(l, coleta) ? 'completa' : `faltam ${faltaNaFicha(l, coleta).length}`);

// ── PDF e planilha ───────────────────────────────────────────────────
// A LISTA (PDF deitado) leva o que cabe numa folha; a PLANILHA leva a
// ficha INTEIRA, uma coluna por campo — é ela que vira o livro de registro.
export const COLUNAS_LISTA = Object.freeze(['Quarto', 'Check-in', 'Hóspede', 'Documento', 'Nascimento', 'Gênero', 'Nacionalidade', 'Onde mora', 'Ficha']);

export function linhasDaLista(linhas, coleta = false) {
  return (linhas || []).map(l => [
    txt(l.quarto), br(l.check_in),
    txt(l.nome) + (txt(l.nome_social) ? ` (${txt(l.nome_social)})` : ''),
    txt(l.documento) ? `${txt(l.tipo_documento) || 'Doc'} ${formatarDocumento(l.documento, l.tipo_documento)}` : 'sem documento',
    br(l.data_nascimento), rotuloSexo(l.sexo), nacionalidade(l),
    residencia(l),
    rotuloFicha(l, coleta),
  ]);
}

export const COLUNAS_PLANILHA = Object.freeze([
  'Reserva', 'Quarto', 'Check-in', 'Check-out', 'Situação', 'Titular', 'Nome completo', 'Sobrenome', 'Nome social', 'Gênero', 'Descrição do gênero', 'Raça/cor', 'Deficiência', 'Tipo de deficiência',
  'Nascimento', 'Menor', 'Legalmente incapaz', 'Tipo de documento', 'Documento', 'Nacionalidade', 'Nacionalidade (código)', 'Profissão',
  // 0070: o nome E o código (a API usa o código; quem lê, o nome)
  'Telefone', 'E-mail', 'País onde mora', 'País (código)', 'UF', 'Município', 'Município (código IBGE)', 'Cidade (exterior)',
  'Logradouro', 'Número', 'Complemento', 'Bairro', 'CEP', 'Texto antigo',
  'Motivo da viagem', 'Meio de transporte', 'Procedência', 'Próximo destino', 'Placa', 'Chegada', 'Saída', 'Ficha', 'O que falta',
]);

export function linhasDaPlanilha(linhas, fuso, coleta = false) {
  return (linhas || []).map(l => [
    txt(l.reserva_numero), txt(l.quarto), br(l.check_in), br(l.check_out), l.situacao_reserva === 'concluida' ? 'concluída' : 'confirmada',
    l.principal ? 'sim' : '', txt(l.nome), txt(l.sobrenome), txt(l.nome_social), rotuloSexo(l.sexo), txt(l.genero_descricao), rotuloRaca(l.raca),
    rotuloDeficiencia(l.deficiencia), rotuloTipoDeficiencia(l.tipo_deficiencia),
    br(l.data_nascimento), l.menor ? 'sim' : '', l.legalmente_incapaz ? 'sim' : '', txt(l.tipo_documento), txt(l.documento),
    nacionalidade(l), txt(l.nacionalidade_pais), txt(l.profissao), txt(l.telefone), txt(l.email),
    nomeDoPais(l.residencia_pais), txt(l.residencia_pais), txt(l.municipio_id) ? txt(l.uf) : '', txt(l.municipio), txt(l.municipio_id),
    txt(l.residencia_pais) && txt(l.residencia_pais) !== BRASIL ? txt(l.cidade) : '',
    txt(l.logradouro), txt(l.numero_endereco), txt(l.complemento), txt(l.bairro), txt(l.cep),
    Object.entries(textoAntigo(l)).map(([c, v]) => `${ROTULOS[c] || c}: ${v}`).join('; '),
    rotuloMotivo(l.motivo_viagem), rotuloTransporte(l.meio_transporte), txt(l.procedencia), txt(l.destino), txt(l.placa_veiculo),
    hora(l.chegada_em, fuso), hora(l.saida_em, fuso), rotuloFicha(l, coleta), faltas(l, coleta).join(', '),
  ]);
}

// ── a ficha de UMA pessoa (o quadro e o PDF individual) ──────────────
// Pares [rótulo, valor] em blocos, na ordem da ficha. Valor vazio sai
// "—": a ficha impressa mostra o buraco, não esconde a linha.
export function fichaDePessoa(l, hoje, fuso) {
  const v = x => txt(x) || '—';
  const i = idade(l.data_nascimento, hoje);
  return [
    ['Identificação', [
      ['Nome completo', v(l.nome)], ['Nome social', v(l.nome_social)],
      // 0065: "Outro — <descrição>", como a pessoa declarou
      ['Gênero', v([rotuloSexo(l.sexo), txt(l.genero_descricao)].filter(Boolean).join(' — '))], ['Raça/cor', v(rotuloRaca(l.raca))],
      ['Deficiência', v([rotuloDeficiencia(l.deficiencia), rotuloTipoDeficiencia(l.tipo_deficiencia)].filter(Boolean).join(' — '))],
      ['Nascimento', l.data_nascimento ? `${br(l.data_nascimento)}${i !== null ? ` (${i} anos)` : ''}` : '—'],
      ['Documento', txt(l.documento) ? `${txt(l.tipo_documento) || 'Doc'} ${formatarDocumento(l.documento, l.tipo_documento)}` : '—'],
      ['Nacionalidade', v(nacionalidade(l))], ['Profissão', v(l.profissao)],
      ['Legalmente incapaz', l.legalmente_incapaz ? 'sim' : 'não'],
      ['Menor de idade no check-in', l.menor ? 'sim' : 'não'],
    ]],
    ['Contato e residência', [
      ['Telefone', v(l.telefone)], ['E-mail', v(l.email)],
      // 0070: o país, e o município (no Brasil) ou a cidade (fora); o
      // texto antigo, se houver, numa linha à parte — não se mistura
      ['País onde mora', v(nomeDoPais(l.residencia_pais))],
      l.residencia_pais && l.residencia_pais !== BRASIL
        ? ['Cidade', v(l.cidade)]
        : ['Município', v(txt(l.municipio) ? `${txt(l.municipio)}/${txt(l.uf)}` : '')],
      ['Logradouro', v(l.logradouro)], ['Número', v(l.numero_endereco)], ['Complemento', v(l.complemento)], ['Bairro', v(l.bairro)],
      ['CEP', v(l.residencia_pais === BRASIL ? formatarCep(l.cep) : l.cep)],
      ...(Object.keys(textoAntigo(l)).length ? [['Texto antigo', Object.entries(textoAntigo(l)).map(([c, x]) => `${ROTULOS[c] || c}: ${x}`).join('; ')]] : []),
    ]],
    ['Viagem', [
      ['Procedência', v(l.procedencia)], ['Próximo destino', v(l.destino)],
      ['Motivo da viagem', v(rotuloMotivo(l.motivo_viagem))], ['Meio de transporte', v(rotuloTransporte(l.meio_transporte))],
      ['Placa do veículo', v(l.placa_veiculo)],
    ]],
    ['Hospedagem', [
      ['Quarto', v(l.quarto)], ['Reserva', v(l.reserva_numero)],
      ['Check-in previsto', br(l.check_in) || '—'], ['Check-out previsto', br(l.check_out) || '—'],
      ['Chegada registrada', hora(l.chegada_em, fuso) || '—'], ['Saída registrada', hora(l.saida_em, fuso) || '—'],
      ['Acompanhantes na reserva', String(Math.max(0, Number(l.pessoas || 0) - 1))],
    ]],
  ];
}

export const TOTAL_OBRIGATORIOS = CAMPOS_FNRH_OBRIGATORIOS.length;

// ── 0071: os envios à API da FNRH ────────────────────────────────────
// A fila mora no banco (`fnrh_envios`); a tela só lê `fila_fnrh` e mostra.
// Quem envia é a Edge Function `fnrh`, disparada pela tela logo depois do
// check-in e do check-out (decisão do Hugo, 29/09/2026: "a tela, na hora").
// 0093 (tempo_fnrh-4): `divergencia` não é envio — é o que a FNRH diz e o
// hotel não (reserva cancelada com hóspede registrado; check-out desfeito
// depois de enviado). Não se reenvia: acerta-se no portal da FNRH.
export const EVENTOS_ENVIO = Object.freeze({ registrar: 'Registro da reserva', checkin: 'Check-in', checkout: 'Check-out', divergencia: 'Divergência' });
export const SITUACOES_ENVIO = Object.freeze({
  pendente:  { rotulo: 'aguardando envio', classe: 'badge-gray' },
  enviando:  { rotulo: 'enviando',          classe: 'badge-blue' },
  enviado:   { rotulo: 'enviado',           classe: 'badge-green' },
  erro:      { rotulo: 'erro',              classe: 'badge-red' },
  sem_cpf:   { rotulo: 'sem CPF',           classe: 'badge-orange' },
  cancelado: { rotulo: 'cancelado',         classe: 'badge-gray' },
  divergencia: { rotulo: 'acertar no portal', classe: 'badge-red' },
});
export const situacaoDoEnvio = s => SITUACOES_ENVIO[s] || { rotulo: String(s || '—'), classe: 'badge-gray' };
export const rotuloEvento = e => EVENTOS_ENVIO[e] || String(e || '—');

// Só o que deu erro ou ficou sem CPF se reenvia — a mesma régua de
// `fnrh_reenviar` (0071); e só quem edita o Check-in.
export const podeReenviar = (e, podeEditar) => !!podeEditar && !!e && (e.situacao === 'erro' || e.situacao === 'sem_cpf');

// O que explica o item, em uma linha, para quem está no balcão.
export function explicacaoDoEnvio(e) {
  if (!e) return '';
  if (e.situacao === 'sem_cpf') return 'Quem fez este registro não tem CPF cadastrado em Usuários. Alguém com CPF clica Reenviar.';
  if (e.situacao === 'erro') return e.erro ? `A FNRH recusou: ${e.erro}` : 'A FNRH não respondeu.';
  if (e.situacao === 'pendente') return 'Sai no próximo envio.';
  if (e.situacao === 'divergencia') return e.erro || 'A FNRH e o hotel não dizem o mesmo — acerte no portal da FNRH.';
  return '';
}

// A resposta da Edge Function → o aviso da tela (null = nada a dizer).
export function avisoDoDisparo(r) {
  if (!r || !r.ligado) return null;
  const n = Number(r.erros || 0);
  if (!n) return null;
  return `${n} envio(s) à FNRH com erro — ficam pendentes e saem de novo no próximo envio. Veja em FNRH.`;
}
